﻿//import '../../../js/app.css';

import { ReservationDiffContainer, type ReservationPayment, type ReservationPosItem, type ReservationRefund, type ReservationState } from './types';

import { multiplyAndRemoveDecimal } from '@/utils/utils';

import { useBulkState, useModelState, useModelStateList } from '@/utils/useModelState';
import { default as classNames } from "classnames";

import { format, differenceInDays, differenceInHours } from 'date-fns';
import { ReactNode, useState } from 'react';
import { RoomPriceDialogComponent } from './RoomPriceDialogComponent';
import { ProgramPriceDialogComponent } from './ProgramPriceDialogComponent';
import { PaymentTypes } from './PaymentTypes/PaymentType';
import { RefundTypes } from './RefundTypes/RefundType';
import { countCalendarDaysBetween, decimalToTHB, longToTHB, stringIsNullOrEmpty } from '@/utils/utils';

import Modal from 'react-bootstrap/Modal';
import { AdjustPaymentDialogComponent } from './AdjustPaymentDialogComponent';
import { AdjustRefundDialogComponent } from './AdjustRefundDialogComponent';
import { BadgeComponent } from './BadgeComponent';
import { EditPosItemDialogComponent } from './EditPosItemDialogComponent';
import { usePrintSummary } from './ClientSettingsContext';

export function OrderDetailsComponent({
    reservationState,
    readonly = false,
    summaryOnly = false,
    hideDiffs = false,
    hideStatements = false,
    hidePayments = false,
    hidePosItems = false,
    hideCardWrapper = false,
    hideOrderHeader = false,
    customHeader
}: OrderDetailsComponentProps) {

    const [isPriceLoading] = useModelState(reservationState.IsPriceLoading);
    const { supportsPrintSummaries, programDurationLabel } = usePrintSummary();

    useBulkState(
        reservationState.RoomBookings,
        [
            rb => rb.RoomType,
            rb => rb.CalculatedPrice
        ]);

    useBulkState(
        reservationState.ProgramBookings,
        [
            pb => pb.ProgramType,
            pb => pb.CalculatedPrice
        ]);

    useModelStateList(reservationState.Payments);
    useModelStateList(reservationState.Refunds);

    useModelState(reservationState.PosItems);

    const [orderDiffs,] = useModelState(reservationState.OrderDiffs);

    const hasNewDiffs = orderDiffs.filter(diff => diff.IsNew).length > 0;

    // Conditional rendering flags
    const shouldShowDiffs = !summaryOnly && !hideDiffs && !stringIsNullOrEmpty(reservationState.ReservationId) && orderDiffs.length > 1;
    const shouldShowPayments = !summaryOnly && !hidePayments;
    const shouldShowPosItems = !summaryOnly && !hidePosItems;
    const shouldShowStatements = !hideStatements;

    const detailRows: JSX.Element[] = [];

    const roomBookingPrices: JSX.Element[] = [];
    const programBookingPrices: JSX.Element[] = [];
    const posItemPrices: JSX.Element[] = [];
    let totalBaseAmount = 0;
    let totalPayableAmount = 0;

    const [dialog, setDialog] = useState<JSX.Element | null>(null);

    function printStatement(diff?: ReservationDiffContainer | null) {
        if (diff) {
            var versionsParam = diff.Versions.map(v => `version=${v}`).join('&');
            window.open(`/statements/${reservationState.ReservationId}?single=true&${versionsParam}`, '_blank');
        } else {
            window.open(`/statements/${reservationState.ReservationId}`, '_blank');
        }
    }

    function printSummary() {
        window.open(`/quotes/${reservationState.ReservationId}?summary=true`, '_blank');
    }

    let balanceCarriedForward = 0;

    let index = 0;

    if (shouldShowDiffs) {
        for (const diff of orderDiffs) {

            index++;

            let diffBaseAmount = 0;
            let diffPayableAmount = 0;

            if (orderDiffs.length > 1) {
                detailRows.push((
                    <tr key={`diff_${index}`} className="bg-light">
                        <td colSpan={3} className="font-bold">
                            <div className="flex justify-between">
                                <span>
                                    #{reservationState.ReservationNumber}-{index}
                                </span>
                                {diff.IsNew && (
                                    <div className="flex gap-2">
                                        <BadgeComponent color="danger">Unsaved</BadgeComponent>
                                    </div>
                                )}
                                {!diff.IsNew && shouldShowStatements && (
                                    <div className="flex gap-2">
                                        <button className="btn btn-sm btn-primary" onClick={() => printStatement(diff)}>
                                            {diff.Transactions.length > 0 ? 'Statement' : 'Quote'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </td>
                    </tr>
                ));

                if (balanceCarriedForward !== 0) {
                    detailRows.push((
                        <tr className="cursor-pointer">
                            <td className="flex flex-row gap-2">
                                <span>Current Balance</span>
                            </td>
                            <td className="text-right">
                                &nbsp;
                            </td>
                            <td className="text-right">
                                {longToTHB(balanceCarriedForward)}
                            </td>
                        </tr>
                    ));
                }
            }

            let rbDiffIndex = 0;

            for (const rbDiff of diff.Rooms) {

                const rb = rbDiff.RoomBooking;

                diffBaseAmount += multiplyAndRemoveDecimal(rb.CalculatedPrice.value.totalBaseAmount);
                diffPayableAmount += multiplyAndRemoveDecimal(rb.CalculatedPrice.value.totalPayableAmount);

                const nights = countCalendarDaysBetween(rb.CheckIn.value, rb.CheckOut.value) - 1;
                const nightText = nights === 1 ? 'night' : 'nights';

                detailRows.push((
                    <tr key={`rbDiff_${index}_${rb.ReservationRoomId}_${rbDiffIndex}`} className="cursor-pointer"
                        onClick={() => {
                            setDialog(<RoomPriceDialogComponent reservationState={reservationState} roomBookingState={rb} visible={true} closed={() => setDialog(null)} />);
                        }}>
                        <td className="flex gap-2">
                            {getActionIcon(rbDiff.Action)}
                            {getRoomTypeDisplayName(reservationState, rb.RoomType.value)} - {format(rb.CheckIn.value, 'dd/MM/yyyy')} - {format(rb.CheckOut.value, 'dd/MM/yyyy')} ({nights} {nightText})
                        </td>
                        {(rb.CalculatedPrice.value.totalBaseAmount > rb.CalculatedPrice.value.totalPayableAmount) && (
                            <td className="text-right line-through">
                                {rb.CalculatedPrice.value.totalBaseAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                            </td>
                        )}
                        {!(rb.CalculatedPrice.value.totalBaseAmount > rb.CalculatedPrice.value.totalPayableAmount) && (
                            <td className="text-right">
                                &nbsp;
                            </td>
                        )}
                        <td className="text-right">
                            {rb.CalculatedPrice.value.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                        </td>
                    </tr>
                ))

                rbDiffIndex++;
            }

            let pbDiffIndex = 0;

            for (const pbDiff of diff.Programs) {

                const pb = pbDiff.ProgramBooking;

                diffBaseAmount += multiplyAndRemoveDecimal(pb.CalculatedPrice.value.totalBaseAmount);
                diffPayableAmount += multiplyAndRemoveDecimal(pb.CalculatedPrice.value.totalPayableAmount);

                const days = countCalendarDaysBetween(pb.Start.value, pb.End.value);
                const daysText = days === 1 ? 'day' : 'days';

                detailRows.push((
                    <tr key={`pbDiff_${index}_${pb.ReservationProgramId}_${pbDiffIndex}`} className="cursor-pointer"
                        onClick={() => {
                            setDialog(<ProgramPriceDialogComponent reservationState={reservationState} programBookingState={pb} visible={true} closed={() => setDialog(null)} />);
                        }}>
                        <td className="flex gap-2">
                            {getActionIcon(pbDiff.Action)}
                            {getProgramTypeDisplayName(reservationState, pb.ProgramType.value)} - {format(pb.Start.value, 'dd/MM/yyyy')} - {format(pb.End.value, 'dd/MM/yyyy')} ({programDurationLabel?.(pb.Start.value, pb.End.value) ?? `${days} ${daysText}`})
                        </td>
                        {(pb.CalculatedPrice.value.totalBaseAmount > pb.CalculatedPrice.value.totalPayableAmount) && (
                            <td className="text-right line-through">
                                {pb.CalculatedPrice.value.totalBaseAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                            </td>
                        )}
                        {!(pb.CalculatedPrice.value.totalBaseAmount > pb.CalculatedPrice.value.totalPayableAmount) && (
                            <td className="text-right">
                                &nbsp;
                            </td>
                        )}
                        <td className="text-right">
                            {pb.CalculatedPrice.value.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                        </td>
                    </tr>
                ))

                pbDiffIndex++;
            }

            let liDiffIndex = 0;

            for (const liDiff of diff.PosItems) {

                const li = liDiff.PosItem;

                const price = multiplyAndRemoveDecimal(li.Amount) * li.Quantity;

                diffBaseAmount += price;
                diffPayableAmount += price;

                detailRows.push((
                    <tr key={`liDiff_${index}_${detailRows.length}_${liDiffIndex}`} className="cursor-pointer">
                        <td className="flex flex-row gap-2">
                            {getActionIcon(liDiff.Action)}
                            <span>{li.Quantity}</span>
                            <span>x</span>
                            <span>{li.Description}</span>
                        </td>
                        <td className="text-right">
                            &nbsp;
                        </td>
                        <td className="text-right">
                            {longToTHB(price)}
                        </td>
                    </tr>
                ));

                liDiffIndex++;
            }

            if (orderDiffs.length > 1) {

                detailRows.push((
                    <tr key={`diff_${index}_subtotal`}>
                        <td>
                            Subtotal
                        </td>
                        {diffBaseAmount !== diffPayableAmount && (
                            <td className="text-right line-through">
                                {longToTHB(diffBaseAmount)}
                            </td>
                        )}
                        {diffBaseAmount === diffPayableAmount && (
                            <td className="text-right">
                                &nbsp;
                            </td>
                        )}
                        <td className="text-right">
                            {longToTHB(diffPayableAmount)}
                        </td>
                    </tr>
                ));
            }

            let transactionsTotal = 0;

            for (let i = 0; i < diff.Transactions.length; i++) {

                const li = diff.Transactions[i];

                transactionsTotal += multiplyAndRemoveDecimal(li.Amount);

                detailRows.push((
                    <tr key={`transaction_${index}_${i}`} className="cursor-pointer">
                        <td className="flex flex-row gap-2">
                            <span>{li.Description}</span>
                        </td>
                        <td className="text-right">
                            &nbsp;
                        </td>
                        <td className="text-right">
                            {decimalToTHB(li.Amount)}
                        </td>
                    </tr>
                ));
            }

            const total = diffPayableAmount - transactionsTotal + balanceCarriedForward;

            if (orderDiffs.length > 1) {

                detailRows.push((
                    <tr key={`diff_${index}_amountDue`}>
                        <td>
                            Amount Due
                        </td>
                        <td className="text-right">
                            &nbsp;
                        </td>
                        <td className="text-right">
                            {longToTHB(total)}
                        </td>
                    </tr>
                ));
            }

            balanceCarriedForward = total;

        }
    }

    if (orderDiffs.length > 1) {
        detailRows.push((
            <tr key={`total`}>
                <td className="font-bold" colSpan={3}>
                    <div className="flex justify-between">
                        <span>Summary</span>
                        {shouldShowStatements && (
                            <div className="flex gap-2">
                                {supportsPrintSummaries && (
                                    <button className="btn btn-sm btn-primary" onClick={() => printSummary()}>
                                        Summary
                                    </button>
                                )}
                                <button className="btn btn-sm btn-primary" onClick={() => printStatement()}>Statement</button>
                            </div>
                        )}
                    </div>


                </td>
            </tr>
        ));
    }

    let rbIndex = 0;

    for (const rb of reservationState.RoomBookings.toArray()) {

        totalBaseAmount += Math.round(rb.CalculatedPrice.value.totalBaseAmount * 100);
        totalPayableAmount += Math.round(rb.CalculatedPrice.value.totalPayableAmount * 100);

        const nights = countCalendarDaysBetween(rb.CheckIn.value, rb.CheckOut.value) - 1;

        roomBookingPrices.push((
            <tr key={`rb_${rb.ReservationRoomId}_${rbIndex}`} className="cursor-pointer"
                onClick={() => {
                    setDialog(<RoomPriceDialogComponent reservationState={reservationState} roomBookingState={rb} visible={true} closed={() => setDialog(null)} />);
                }}>
                <td>
                    {getRoomTypeDisplayName(reservationState, rb.RoomType.value)} - {format(rb.CheckIn.value, 'dd/MM/yyyy')} - {format(rb.CheckOut.value, 'dd/MM/yyyy')} ({nights} nights)
                </td>
                {(rb.CalculatedPrice.value.totalBaseAmount > rb.CalculatedPrice.value.totalPayableAmount) && (
                    <td className="text-right line-through">
                        {rb.CalculatedPrice.value.totalBaseAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                    </td>
                )}
                {!(rb.CalculatedPrice.value.totalBaseAmount > rb.CalculatedPrice.value.totalPayableAmount) && (
                    <td className="text-right">
                        &nbsp;
                    </td>
                )}
                <td className="text-right">
                    {rb.CalculatedPrice.value.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                </td>
            </tr>
        ))

        rbIndex++;
    }

    let pbIndex = 0;

    for (const rb of reservationState.ProgramBookings.toArray()) {

        totalBaseAmount += Math.round(rb.CalculatedPrice.value.totalBaseAmount * 100);
        totalPayableAmount += Math.round(rb.CalculatedPrice.value.totalPayableAmount * 100);

        const days = countCalendarDaysBetween(rb.Start.value, rb.End.value);

        programBookingPrices.push((
            <tr key={`rb_${rb.ReservationProgramId}_${pbIndex}`} className="cursor-pointer"
                onClick={() => {
                    setDialog(<ProgramPriceDialogComponent reservationState={reservationState} programBookingState={rb} visible={true} closed={() => setDialog(null)} />);
                }}>
                <td>
                    {getProgramTypeDisplayName(reservationState, rb.ProgramType.value)} - {format(rb.Start.value, 'dd/MM/yyyy')} - {format(rb.End.value, 'dd/MM/yyyy')} ({programDurationLabel?.(rb.Start.value, rb.End.value) ?? `${days} days`})
                </td>
                {(rb.CalculatedPrice.value.totalBaseAmount > rb.CalculatedPrice.value.totalPayableAmount) && (
                    <td className="text-right line-through">
                        {rb.CalculatedPrice.value.totalBaseAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                    </td>
                )}
                {!(rb.CalculatedPrice.value.totalBaseAmount > rb.CalculatedPrice.value.totalPayableAmount) && (
                    <td className="text-right">
                        &nbsp;
                    </td>
                )}
                <td className="text-right">
                    {rb.CalculatedPrice.value.totalPayableAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}
                </td>
            </tr>
        ))

        pbIndex++;
    }

    const [editPosItemDialogPosItem, setEditPosItemDialogPosItem] = useState<ReservationPosItem | null>(null);

    let editPosItemDialogComponent: ReactNode = null;

    if (editPosItemDialogPosItem) {
        editPosItemDialogComponent = (
            <Modal centered backdrop="static" show={true} onHide={() => setEditPosItemDialogPosItem(null)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Adjust Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <EditPosItemDialogComponent
                        reservationState={reservationState}
                        posItem={editPosItemDialogPosItem}
                        close={() => setEditPosItemDialogPosItem(null)} />
                </Modal.Body>
            </Modal>
        );
    }

    let liIndex = 0;

    if (shouldShowPosItems) {
        for (const li of reservationState.PosItems.value) {

            const price = multiplyAndRemoveDecimal(li.Amount) * li.Quantity;
            totalBaseAmount += price;
            totalPayableAmount += price;

            posItemPrices.push((
                <tr key={`li_${posItemPrices.length}_${liIndex}`} className="cursor-pointer">
                    <td className="flex flex-row gap-2">
                        <span>{li.Quantity}</span>
                        <span>x</span>
                        <span>{li.Description}</span>
                        {!readonly && (
                            <button
                                className="border-0 bg-transparent p-0 text-current"
                                disabled={editPosItemDialogPosItem !== null}
                                onClick={() => setEditPosItemDialogPosItem(li)}>
                                <i className="fas fa-pencil-alt"></i>
                            </button>
                        )}
                    </td>
                    <td className="text-right">
                        &nbsp;
                    </td>
                    <td className="text-right">
                        {longToTHB(price)}
                    </td>

                </tr>
            ))

            liIndex++;
        }
    }

    const [adjustPaymentDialogPayment, setAdjustPaymentDialogPayment] = useState<ReservationPayment | null>(null);

    let adjustPaymentDialogComponent: ReactNode = null;

    if (adjustPaymentDialogPayment) {
        adjustPaymentDialogComponent = (
            <Modal centered backdrop="static" show={true} onHide={() => setAdjustPaymentDialogPayment(null)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Adjust Payment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AdjustPaymentDialogComponent
                        reservationState={reservationState}
                        payment={adjustPaymentDialogPayment}
                        close={() => setAdjustPaymentDialogPayment(null)} />
                </Modal.Body>
            </Modal>
        );
    }

    const [adjustRefundDialogRefund, setAdjustRefundDialogRefund] = useState<ReservationRefund | null>(null);

    let adjustRefundDialogComponent: ReactNode = null;

    if (adjustRefundDialogRefund) {
        adjustRefundDialogComponent = (
            <Modal centered backdrop="static" show={true} onHide={() => setAdjustRefundDialogRefund(null)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Adjust Refund</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <AdjustRefundDialogComponent
                        reservationState={reservationState}
                        refund={adjustRefundDialogRefund}
                        close={() => setAdjustRefundDialogRefund(null)} />
                </Modal.Body>
            </Modal>
        );
    }

    const payments: ReactNode[] = [];
    let paymentsTotalAmount = 0;

    if (shouldShowPayments) {
        reservationState.Payments.toArray().forEach(p => {

            if (p.Amount === 0) {
                return;
            }

            payments.push((
                <tr key={`${p.PaymentId}_amount`}>
                    <td className="flex flex-row gap-2">
                        <span>Payment - {PaymentTypes.getPaymentTypeName(p.PaymentType)}</span>
                        {!readonly && false && (
                            <button
                                className="border-0 bg-transparent p-0 text-current"
                                disabled={adjustPaymentDialogPayment !== null}
                                onClick={() => setAdjustPaymentDialogPayment(p)}>
                                <i className="fas fa-pencil-alt"></i>
                            </button>
                        )}
                    </td>
                    <td colSpan={2} className="text-right">{p.Amount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}</td>
                </tr>
            ));

            //if (p.FeeAmount > 0) {
            //    payments.push(
            //        <tr key={`${p.PaymentId}_fee`}>
            //            <td>Payment Fee</td>
            //            <td colSpan={2} className="text-right">{p.FeeAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}</td>
            //        </tr>
            //    );
            //}

            paymentsTotalAmount += Math.round(p.Amount * 100);
        });
    }

    const [removingRefund, setRemovingRefund] = useState(false);

    const refunds: ReactNode[] = [];
    let refundsTotalAmount = 0;

    if (shouldShowPayments) {
        reservationState.Refunds.toArray().forEach(p => {

            if (p.Amount === 0) {
                return;
            }

            refunds.push((
                <tr key={`${p.RefundId}_amount`}>
                    <td className="flex flex-row gap-2">
                        <span>Refund - {RefundTypes.getRefundTypeName(p.RefundType)}</span>
                        {!readonly && false && (
                            <button
                                className="border-0 bg-transparent p-0 text-current"
                                disabled={removingRefund}
                                onClick={() => setAdjustRefundDialogRefund(p)}>
                                <i className="fas fa-pencil-alt"></i>
                            </button>
                        )}
                    </td>
                    <td colSpan={2} className="text-right">{p.Amount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}</td>
                </tr>
            ));

            //if (p.FeeAmount > 0) {
            //    refunds.push(
            //        <tr key={`${p.RefundId}_fee`}>
            //            <td>Refund Fee</td>
            //            <td colSpan={2} className="text-right">{p.FeeAmount.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}</td>
            //        </tr>
            //    );
            //}

            refundsTotalAmount += Math.round(p.Amount * 100);
        });
    }

    const hasDiscounts = totalPayableAmount < totalBaseAmount;
    const amountDue = totalPayableAmount - paymentsTotalAmount + refundsTotalAmount;

    const tableContent = (
        <table className="mb-0 table">
            <tbody>
                {detailRows}
                {roomBookingPrices}
                {programBookingPrices}
                {posItemPrices}
                {/*<div className={classNames({ 'line-through': totalDiscountedPrice !== totalFullPrice })}>*/}
                {/*    {totalFullPrice.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}*/}
                {/*</div>*/}
                {/*{totalDiscountedPrice !== totalFullPrice && (*/}
                {/*    <div>*/}
                {/*        {totalDiscountedPrice.toLocaleString('en-NZ', { style: 'currency', currency: 'THB' })}*/}
                {/*    </div>*/}
                {/*)}*/}
                <tr>
                    <td>
                        Total
                    </td>
                    {hasDiscounts && (
                        <td className="text-right line-through">
                            {longToTHB(totalBaseAmount)}
                        </td>
                    )}
                    {!hasDiscounts && (
                        <td className="text-right">
                            &nbsp;
                        </td>
                    )}
                    <td className="text-right">
                        {longToTHB(totalPayableAmount)}
                    </td>
                </tr>
                {payments}
                {refunds}
                {shouldShowPayments && (
                    <tr>
                        <td>Amount Due</td>
                        <td colSpan={2} className="text-right">{longToTHB(amountDue)}</td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    if (hideCardWrapper) {
        return (
            <div>
                {dialog}
                {adjustPaymentDialogComponent}
                {adjustRefundDialogComponent}
                {editPosItemDialogComponent}
                {tableContent}
            </div>
        );
    }

    return (
        <div className="card">
            {!hideOrderHeader && (
                <div className="card-header flex items-center gap-2">
                    <span>
                        {customHeader || "Order Details"}
                    </span>
                    {amountDue > 0 && shouldShowPayments && (
                        <BadgeComponent color="warning">Payment Due</BadgeComponent>
                    )}
                    {amountDue < 0 && shouldShowPayments && (
                        <BadgeComponent color="warning">Refund Due</BadgeComponent>
                    )}
                    {isPriceLoading && (
                        <BadgeComponent color="warning">
                            <span className="flex gap-2">
                                <div className="spinner-border" role="status" style={{ width: '1rem', height: '1rem' }}>
                                    <span className="visually-hidden">Loading</span>
                                </div>
                                <span>
                                    Recalculating price..
                                </span>
                            </span>
                        </BadgeComponent>
                    )}
                    {hasNewDiffs && orderDiffs.length === 1 && (
                        <BadgeComponent color="danger">Unsaved</BadgeComponent>
                    )}
                </div>
            )}
            <div className="card-body">
                <div>
                    {dialog}
                    {adjustPaymentDialogComponent}
                    {adjustRefundDialogComponent}
                    {editPosItemDialogComponent}
                    {tableContent}
                </div>
            </div>
        </div>
    )
}

export function getActionIcon(action: 'None' | 'Added' | 'Removed' | 'Updated'): ReactNode {
    switch (action) {
        case 'Added':
            return <i className="fas fa-plus-circle text-success" style={{ fontSize: '0.8rem', marginTop: '0.1rem', alignSelf: 'center' }}></i>;
        case 'Removed':
            return <i className="fas fa-minus-circle text-danger" style={{ fontSize: '0.8rem', marginTop: '0.1rem', alignSelf: 'center' }}></i>;
        case 'Updated':
            return <i className="fas fa-pencil-alt text-warning" style={{ fontSize: '0.8rem', marginTop: '0.1rem', alignSelf: 'center' }}></i>;
        default:
            return null;
    }
}

export interface OrderDetailsComponentProps {
    reservationState: ReservationState;
    readonly?: boolean;
    // New display control props
    summaryOnly?: boolean;           // Show only room/program bookings + total (no diffs, payments, etc.)
    hideDiffs?: boolean;             // Hide order diffs section
    hideStatements?: boolean;        // Hide statement buttons and diff headers
    hidePayments?: boolean;          // Hide payments/refunds section
    hidePosItems?: boolean;          // Hide POS items section
    hideCardWrapper?: boolean;       // Render as table only, no card wrapper
    hideOrderHeader?: boolean;       // Hide "Order Details" header and badges
    customHeader?: string;           // Custom header text (e.g., "Rate Comparison")
}

export function getRoomTypeDisplayName(
    reservation: ReservationState,
    roomType: string): string {
    if (roomType in reservation.RoomTypes) {
        return reservation.RoomTypes[roomType].Name;
    }

    return roomType;
}

export function getProgramTypeDisplayName(
    reservation: ReservationState,
    programType: string): string {
    if (programType in reservation.ProgramTypes) {
        return reservation.ProgramTypes[programType].Name;
    }

    return programType;
}
